import { getCampsiteCategoriesPresenter } from '../presenter-json/CampsiteCategoriesUpdaterJson'
import type { CampsiteCategory } from '~/utility/types/CampsiteCategory'

export interface CategoryFacetRelationship {
  categoryId: string
  requiredFacets: string[]
  isByo: boolean
}

export interface CategoryFacetValidationResult {
  isValid: boolean
  correctedCategoryIds: string[]
  correctedFacets: string[]
  removedCategories: string[]
  removedFacets: string[]
}

/**
 * Service for managing category-facet relationships and ensuring consistency
 * between selected categories and their required facets.
 */
export class CategoryFacetLinker {
  private categories: CampsiteCategory[] = []
  private relationshipMap: Map<string, CategoryFacetRelationship> = new Map()

  constructor(language: string = 'en-gb') {
    this.initializeRelationships(language)
  }

  private initializeRelationships(language: string): void {
    const presenter = getCampsiteCategoriesPresenter(language)
    this.categories = presenter.list

    // Build relationship map for BYO categories with subcategories
    for (const category of this.categories) {
      if (
        category.isByo &&
        category.subcategories &&
        category.subcategories.length > 0
      ) {
        const requiredFacets = category.subcategories
          .map((sub) => sub.facetSlug)
          .filter(Boolean)

        this.relationshipMap.set(category.id, {
          categoryId: category.id,
          requiredFacets,
          isByo: true,
        })
      }
    }
  }

  /**
   * Gets the parent category for a given facet slug
   */
  getParentCategoryForFacet(facetSlug: string): CampsiteCategory | null {
    for (const category of this.categories) {
      if (category.subcategories && category.subcategories.length > 0) {
        const matchingSubcategory = category.subcategories.find(
          (sub) => sub.facetSlug === facetSlug,
        )
        if (matchingSubcategory) {
          return category
        }
      }
    }
    return null
  }

  /**
   * Gets all available facets for a given category
   */
  getAvailableFacetsForCategory(categoryId: string): string[] {
    const relationship = this.relationshipMap.get(categoryId)
    return relationship ? relationship.requiredFacets : []
  }

  /**
   * Checks if a category requires facets to be meaningful
   */
  categoryRequiresFacets(categoryId: string): boolean {
    const relationship = this.relationshipMap.get(categoryId)
    return relationship ? relationship.requiredFacets.length > 0 : false
  }

  /**
   * Checks if a category should be preserved even without facets
   * This includes non-BYO categories and potentially route-based categories
   */
  shouldPreserveCategoryWithoutFacets(categoryId: string): boolean {
    const category = this.categories.find((cat) => cat.id === categoryId)
    if (!category) return false

    // Non-BYO categories don't require facets
    if (!category.isByo) return true

    // BYO categories without subcategories don't require facets
    if (!category.subcategories || category.subcategories.length === 0)
      return true

    return false
  }

  /**
   * Validates category-facet combinations and provides corrections
   * @param preserveSingleCategories - If true, single categories without facets are preserved (for route-based categories)
   */
  validateAndCorrect(
    categoryIds: string[],
    facets: string[],
    routeBasedCategoryIds: string[] = [],
    preserveSingleCategories: boolean = false,
  ): CategoryFacetValidationResult {
    const result: CategoryFacetValidationResult = {
      isValid: true,
      correctedCategoryIds: [...categoryIds],
      correctedFacets: [...facets],
      removedCategories: [],
      removedFacets: [],
    }

    // Special case: if we have exactly one category and no facets, and preserveSingleCategories is true
    // This handles route-based category pages like /motorhomes/
    if (
      preserveSingleCategories &&
      categoryIds.length === 1 &&
      facets.length === 0
    ) {
      return result
    }

    // Handle orphaned categories (categories selected without required facets)
    for (const categoryId of categoryIds) {
      // Skip route-based categories - they should always be preserved
      if (routeBasedCategoryIds.includes(categoryId)) {
        continue
      }

      // Check if this category requires facets
      if (!this.categoryRequiresFacets(categoryId)) {
        continue
      }

      const relationship = this.relationshipMap.get(categoryId)
      if (relationship && relationship.requiredFacets.length > 0) {
        // Check if any of the required facets are selected
        const hasRequiredFacet = relationship.requiredFacets.some((facet) =>
          facets.includes(facet),
        )

        if (!hasRequiredFacet) {
          // Remove orphaned category
          result.correctedCategoryIds = result.correctedCategoryIds.filter(
            (id) => id !== categoryId,
          )
          result.removedCategories.push(categoryId)
          result.isValid = false
        }
      }
    }

    // Handle orphaned facets (facets selected without their parent category)
    for (const facet of facets) {
      const parentCategory = this.getParentCategoryForFacet(facet)
      if (
        parentCategory &&
        !result.correctedCategoryIds.includes(parentCategory.id)
      ) {
        // Remove orphaned facet
        result.correctedFacets = result.correctedFacets.filter(
          (f) => f !== facet,
        )
        result.removedFacets.push(facet)
        result.isValid = false
      }
    }

    return result
  }

  /**
   * Automatically adds required category when a facet is selected
   */
  addFacetWithCategory(
    currentCategoryIds: string[],
    currentFacets: string[],
    newFacet: string,
  ): { categoryIds: string[]; facets: string[] } {
    const parentCategory = this.getParentCategoryForFacet(newFacet)

    if (!parentCategory) {
      return {
        categoryIds: currentCategoryIds,
        facets: [...currentFacets, newFacet],
      }
    }

    // For BYO categories with facets, only allow single category selection
    // when a specific facet is chosen
    let newCategoryIds = currentCategoryIds
    if (parentCategory.isByo) {
      newCategoryIds = [parentCategory.id]
    } else if (!currentCategoryIds.includes(parentCategory.id)) {
      newCategoryIds = [...currentCategoryIds, parentCategory.id]
    }

    return {
      categoryIds: newCategoryIds,
      facets: [...currentFacets, newFacet],
    }
  }

  /**
   * Removes category and its dependent facets
   */
  removeCategoryWithFacets(
    currentCategoryIds: string[],
    currentFacets: string[],
    categoryToRemove: string,
  ): { categoryIds: string[]; facets: string[] } {
    const relationship = this.relationshipMap.get(categoryToRemove)

    let newFacets = currentFacets
    if (relationship) {
      // Remove all facets associated with this category
      newFacets = currentFacets.filter(
        (facet) => !relationship.requiredFacets.includes(facet),
      )
    }

    return {
      categoryIds: currentCategoryIds.filter((id) => id !== categoryToRemove),
      facets: newFacets,
    }
  }

  /**
   * Removes facet and optionally its parent category if no other facets remain
   */
  removeFacetWithCategory(
    currentCategoryIds: string[],
    currentFacets: string[],
    facetToRemove: string,
    removeParentIfEmpty: boolean = true,
  ): { categoryIds: string[]; facets: string[] } {
    const parentCategory = this.getParentCategoryForFacet(facetToRemove)
    const newFacets = currentFacets.filter((f) => f !== facetToRemove)

    if (!parentCategory || !removeParentIfEmpty) {
      return {
        categoryIds: currentCategoryIds,
        facets: newFacets,
      }
    }

    // Check if any other facets from this category remain
    const relationship = this.relationshipMap.get(parentCategory.id)
    if (relationship) {
      const remainingFacetsForCategory = relationship.requiredFacets.filter(
        (facet) => newFacets.includes(facet),
      )

      // If no facets remain for this BYO category, remove the category too
      if (remainingFacetsForCategory.length === 0 && parentCategory.isByo) {
        return {
          categoryIds: currentCategoryIds.filter(
            (id) => id !== parentCategory.id,
          ),
          facets: newFacets,
        }
      }
    }

    return {
      categoryIds: currentCategoryIds,
      facets: newFacets,
    }
  }

  /**
   * Gets a human-readable description of what was corrected
   */
  getValidationMessage(result: CategoryFacetValidationResult): string {
    if (result.isValid) {
      return 'All category-facet combinations are valid'
    }

    const messages: string[] = []

    if (result.removedCategories.length > 0) {
      messages.push(
        `Removed categories without required facets: ${result.removedCategories.join(', ')}`,
      )
    }

    if (result.removedFacets.length > 0) {
      messages.push(
        `Removed facets without parent categories: ${result.removedFacets.join(', ')}`,
      )
    }

    return messages.join('; ')
  }
}

// Export singleton instance for easy use across the app
let linkerInstance: CategoryFacetLinker | null = null
let currentLanguage = 'en-gb'

export function getCategoryFacetLinker(
  language: string = 'en-gb',
): CategoryFacetLinker {
  if (!linkerInstance || currentLanguage !== language) {
    linkerInstance = new CategoryFacetLinker(language)
    currentLanguage = language
  }
  return linkerInstance
}

// Reset instance when language changes (for i18n support)
export function resetCategoryFacetLinker(): void {
  linkerInstance = null
  currentLanguage = 'en-gb'
}
